/**
 * TremCare Product Page Animations
 * Handles scroll-based animations, interactions, and responsive behavior
 */

// Simple direct initialization - no class wrapping
(function () {
  'use strict';

  console.log('🚀 TremCare animations loading...');

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    console.log('📱 Initializing TremCare animations');
    setupWatchScene();
    setupAppShowcase();
    setupScrollAnimations();
    setupMobilePullDownFix();
  }

  /**
   * Prevents "pull-to-refresh" and "top bounce" on mobile
   * while maintaining normal scroll behavior.
   */
  function setupMobilePullDownFix() {
    if (window.innerWidth >= 768) return;

    let touchY = 0;
    
    // Use non-passive listener to allow preventDefault
    window.addEventListener('touchstart', (e) => {
      touchY = e.touches[0].clientY;
    }, { passive: false });

    window.addEventListener('touchmove', (e) => {
      const currentY = e.touches[0].clientY;
      const isPullingDown = currentY > touchY;
      
      // If at the absolute top and trying to pull DOWN, block it
      if (window.scrollY <= 0 && isPullingDown) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    }, { passive: false });
  }

  /**
   * Setup Watch Scene Animations
   */
  function setupWatchScene() {
    const watchScene = document.getElementById('tremcare-watch-scene');
    if (!watchScene) return;

    const elements = {
      rig: document.querySelector('.tremcare-watch-rig'),
      upperStrap: document.querySelector('.tremcare-upper-strap'),
      lowerStrap: document.querySelector('.tremcare-lower-strap'),
      stage1: document.querySelector('.tremcare-stage-1-content'),
      stage2: document.querySelector('.tremcare-stage-2-content'),
      stage3: document.querySelector('.tremcare-center-taglines'),
      stage4: document.querySelector('.tremcare-stage-4-content'),
      final: document.querySelector('.tremcare-final-punchline'),
      tagline1: document.querySelector('.tremcare-tagline-1'),
      tagline2: document.querySelector('.tremcare-tagline-2'),
      glow: document.querySelector('.tremcare-glow-bg')
    };

    initializeWatchElements(elements);

    let ticking = false;
    let currentProgress = 0;
    let targetProgress = 0;

    function update() {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobile = width < 768;
      const isSmallDesktop = width < 1440 && width >= 768;
      
      const lerpFactor = isMobile ? 0.4 : 0.15;
      currentProgress += (targetProgress - currentProgress) * lerpFactor;
      
      if (Math.abs(targetProgress - currentProgress) < 0.0001) currentProgress = targetProgress;

      const p = currentProgress;

      // 1. WATCH POSITIONING
      const baseWatchX = isMobile ? 0 : (isSmallDesktop ? 22 : 25);
      const watchOverlapFix = (width < 1200 && !isMobile) ? 5 : 0;
      const finalWatchX = baseWatchX + watchOverlapFix;

      const watchX = interpolate(p,
        [0, 0.20, 0.25, 0.40, 0.45, 0.60, 0.65, 0.80, 0.85, 1.0],
        [0, 0, -finalWatchX, -finalWatchX, 0, 0, finalWatchX, finalWatchX, 0, 0]
      );

      const watchY = interpolate(p,
        [0, 0.20, 0.40, 0.60, 0.80, 1.0],
        isMobile ? [-8, -12, -12, -15, -22, -25] : [5, 0, 0, 0, 0, -10]
      );

      const watchScale = interpolate(p,
        [0, 0.15, 0.25, 0.40, 0.50, 0.65, 0.80, 0.85, 1.0],
        isMobile
          ? [1.4, 0.85, 0.8, 0.8, 0.85, 0.8, 0.75, 0.85, 0.9]
          : [2.2, 1.1, 0.95, 0.95, 1.05, 0.95, 0.9, 1.0, 1.0]
      );

      // 2. STRAP LOGIC
      const strapGap = interpolate(p,
        [0.40, 0.48, 0.55, 0.62],
        isMobile ? [0, 250, 250, 0] : [0, 280, 280, 0]
      );

      const strapOpacity = interpolate(p, [0.82, 0.88], [1, 0]);

      // 3. CONTENT OPACITY
      const stage1Opacity = interpolate(p, [0.0, 0.05, 0.18, 0.22], [0, 1, 1, 0]);
      const stage2Opacity = interpolate(p, [0.22, 0.26, 0.40, 0.44], [0, 1, 1, 0]);
      const stage3Opacity = interpolate(p, [0.44, 0.48, 0.60, 0.64], [0, 1, 1, 0]);
      const stage4Opacity = interpolate(p, [0.64, 0.68, 0.82, 0.86], [0, 1, 1, 0]);
      const finalOpacity = interpolate(p, [0.86, 0.92, 1.0], [0, 1, 1]);

      // 4. CONTENT POSITIONING
      const mobileTextYOffset = 280;
      const stage1Y = interpolate(p, [0.0, 0.10], isMobile ? [mobileTextYOffset - 30, mobileTextYOffset] : [0, 0]);
      const stage2Y = interpolate(p, [0.22, 0.28], isMobile ? [mobileTextYOffset - 30, mobileTextYOffset] : [0, 0]);
      const stage4Y = interpolate(p, [0.64, 0.70], isMobile ? [mobileTextYOffset - 30, mobileTextYOffset] : [0, 0]);
      const taglinesY1 = interpolate(p, [0.44, 0.50], isMobile ? [-180, -200] : [-160, -140]);
      const taglinesY2 = interpolate(p, [0.44, 0.50], isMobile ? [-20, 0] : [160, 140]);
      const finalY = interpolate(p, [0.86, 0.92], isMobile ? [-160, -200] : [50, 0]);

      const stage1X = isMobile ? 0 : interpolate(p, [0.0, 0.10], [0, -20]);
      const stage2X = isMobile ? 0 : interpolate(p, [0.22, 0.28], [20, 0]);
      const stage4X = isMobile ? 0 : interpolate(p, [0.64, 0.70], [-20, 0]);

      // Apply
      if (elements.rig) elements.rig.style.transform = `perspective(1200px) translate(calc(-50% + ${watchX}vw), calc(-50% + ${watchY}vh)) scale(${watchScale})`;
      if (elements.upperStrap) {
        elements.upperStrap.style.transform = `translate(-50%, ${-strapGap}px)`;
        elements.upperStrap.style.opacity = strapOpacity;
      }
      if (elements.lowerStrap) {
        elements.lowerStrap.style.transform = `translate(-50%, ${strapGap}px)`;
        elements.lowerStrap.style.opacity = strapOpacity;
      }

      if (elements.stage1) {
        elements.stage1.style.opacity = stage1Opacity;
        elements.stage1.style.transform = isMobile ? `translate(-50%, ${stage1Y}px)` : `translateX(calc(-50% + (${stage1X}vw)))`;
        if (isMobile) elements.stage1.style.left = '50%';
        elements.stage1.style.display = stage1Opacity > 0.01 ? 'block' : 'none';
      }
      if (elements.stage2) {
        elements.stage2.style.opacity = stage2Opacity;
        elements.stage2.style.transform = isMobile ? `translate(-50%, ${stage2Y}px)` : `translateX(calc(-50% + (${stage2X}vw)))`;
        if (isMobile) elements.stage2.style.left = '50%';
        elements.stage2.style.display = stage2Opacity > 0.01 ? 'block' : 'none';
      }
      if (elements.stage3) {
        elements.stage3.style.opacity = stage3Opacity;
        elements.stage3.style.display = stage3Opacity > 0.01 ? 'flex' : 'none';
        if (elements.tagline1) elements.tagline1.style.transform = `translateY(${taglinesY1}px)`;
        if (elements.tagline2) elements.tagline2.style.transform = `translateY(${taglinesY2}px)`;
      }
      if (elements.stage4) {
        elements.stage4.style.opacity = stage4Opacity;
        elements.stage4.style.transform = isMobile ? `translate(-50%, ${stage4Y}px)` : `translateX(calc(-50% + (${stage4X}vw)))`;
        if (isMobile) elements.stage4.style.left = '50%';
        elements.stage4.style.display = stage4Opacity > 0.01 ? 'block' : 'none';
      }
      if (elements.final) {
        elements.final.style.opacity = finalOpacity;
        elements.final.style.transform = `translateY(${finalY}px)`;
        elements.final.style.display = finalOpacity > 0.01 ? 'block' : 'none';
      }

      if (elements.glow) {
        elements.glow.style.transform = `scale(${interpolate(p, [0, 1], [1, 2.5])})`;
        elements.glow.style.opacity = interpolate(p, [0, 0.5, 0.9], [0.03, 0.06, 0.01]);
      }

      if (Math.abs(targetProgress - currentProgress) > 0.0001) {
        requestAnimationFrame(update);
        ticking = true;
      } else ticking = false;
    }

    function requestTick() {
      const rect = watchScene.getBoundingClientRect();
      const stickyOffset = 72;
      const scrollHeight = rect.height - window.innerHeight;
      let progress = (stickyOffset - rect.top) / scrollHeight;
      targetProgress = Math.max(0, Math.min(1, progress));
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);
    setTimeout(requestTick, 100);
  }

  function initializeWatchElements(elements) {
    if (elements.rig) {
      elements.rig.style.transition = 'none';
      elements.rig.style.willChange = 'transform';
    }
    [elements.stage1, elements.stage2, elements.stage3, elements.stage4, elements.final].forEach(el => {
      if (el) {
        el.style.transition = 'none';
        el.style.willChange = 'opacity, transform';
      }
    });
    if (elements.upperStrap) {
      elements.upperStrap.style.transition = 'none';
      elements.upperStrap.style.transformOrigin = 'bottom center';
    }
    if (elements.lowerStrap) {
      elements.lowerStrap.style.transition = 'none';
      elements.lowerStrap.style.transformOrigin = 'top center';
    }
  }

  /**
   * Linear Interpolation Utility
   * Replicates Framer Motion's useTransform mapping logic
   */
  function interpolate(value, inputRange, outputRange) {
    if (value <= inputRange[0]) return outputRange[0];
    if (value >= inputRange[inputRange.length - 1]) return outputRange[outputRange.length - 1];

    let i = 1;
    while (value > inputRange[i]) i++;

    const minInput = inputRange[i - 1];
    const maxInput = inputRange[i];
    const minOutput = outputRange[i - 1];
    const maxOutput = outputRange[i];

    return minOutput + (value - minInput) * (maxOutput - minOutput) / (maxInput - minInput);
  }


  /**
   * Setup App Showcase Animations
   */
  function setupAppShowcase() {
    const appShowcase = document.getElementById('tremcare-app-showcase');
    if (!appShowcase) return;

    const elements = {
      title: appShowcase.querySelector('.tremcare-app-title'),
      phoneFrame: appShowcase.querySelector('.tremcare-phone-frame'),
      phoneSlides: appShowcase.querySelectorAll('.tremcare-app-slide'),
      contentSlides: appShowcase.querySelectorAll('.tremcare-content-slide'),
      glow: appShowcase.querySelector('.tremcare-phone-glow')
    };

    const colors = ["#0E4B8B", "#3EA524", "#DF7861"];

    let ticking = false;
    let currentProgress = 0;
    let targetProgress = 0;

    function update() {
      const isMobile = window.innerWidth < 768;
      // Snappy lerp: highly responsive
      const lerpFactor = isMobile ? 0.45 : 0.25;
      
      currentProgress += (targetProgress - currentProgress) * lerpFactor;
      if (Math.abs(targetProgress - currentProgress) < 0.0001) currentProgress = targetProgress;

      const p = currentProgress;

      // 1. Title
      if (elements.title) {
        const titleOpacity = interpolate(p, [0, 0.02, 0.94, 0.98], [0, 1, 1, 0]);
        const titleY = interpolate(p, [0.0, 0.02, 0.94, 1], [15, 0, 0, 80]);
        elements.title.style.opacity = titleOpacity;
        elements.title.style.transform = `translateY(${titleY}px)`;
        elements.title.style.display = titleOpacity > 0.001 ? 'block' : 'none';
      }

      // 2. Mockup
      if (elements.phoneFrame) {
        const mockupOpacity = interpolate(p, [0, 0.04, 0.94, 0.98], [0, 1, 1, 0]);
        const mockupY = interpolate(p, [0.94, 1], [0, isMobile ? 300 : 100]);
        
        let dynamicScale = 0.95;
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        if (width >= 1024) {
          if (height < 700) dynamicScale = 0.65;
          else if (height < 800) dynamicScale = 0.75;
          else if (height < 900) dynamicScale = 0.85;
        } else {
          dynamicScale = height < 700 ? 0.8 : 0.9;
        }

        elements.phoneFrame.style.opacity = mockupOpacity;
        elements.phoneFrame.style.transform = `translateY(${mockupY}px) scale(${dynamicScale})`;
      }

      // 3. Active index calculation for color syncing
      let activeIndex = 0;
      if (p < 0.28) activeIndex = 0;
      else if (p < 0.63) activeIndex = 1;
      else activeIndex = 2;

      // 4. Phone Slides (Clean, snappy horizontal sliding)
      elements.phoneSlides.forEach((slide, index) => {
        let x = 0;
        let opacity = 0;

        if (index === 0) {
          x = interpolate(p, [0.20, 0.35], [0, -100]);
          opacity = interpolate(p, [0, 0.20, 0.35], [1, 1, 0]);
        } else if (index === 1) {
          x = interpolate(p, [0.20, 0.35, 0.55, 0.70], [100, 0, 0, -100]);
          opacity = interpolate(p, [0.22, 0.35, 0.55, 0.68], [0, 1, 1, 0]);
        } else if (index === 2) {
          x = interpolate(p, [0.55, 0.70], [100, 0]);
          opacity = interpolate(p, [0.57, 0.70, 1.0], [0, 1, 1]);
        }

        slide.style.opacity = opacity;
        slide.style.transform = `translateX(${x}%)`;
        slide.style.visibility = opacity > 0.001 ? 'visible' : 'hidden';
      });

      // 5. Ambient Glow
      if (elements.glow) {
        elements.glow.style.backgroundColor = colors[activeIndex];
        elements.glow.style.transition = 'background-color 0.8s ease';
      }

      // 6. Content Slides (Synchronized with phone slides)
      elements.contentSlides.forEach((slide, index) => {
        let opacity = 0;
        let y = 0;

        if (index === 0) {
          opacity = interpolate(p, [0, 0.1, 0.20, 0.30], [0, 1, 1, 0]);
          y = interpolate(p, [0, 0.1, 0.20, 0.30], [20, 0, 0, -20]);
        } else if (index === 1) {
          opacity = interpolate(p, [0.22, 0.35, 0.55, 0.68], [0, 1, 1, 0]);
          y = interpolate(p, [0.22, 0.35, 0.55, 0.68], [20, 0, 0, -20]);
        } else if (index === 2) {
          opacity = interpolate(p, [0.57, 0.70, 0.9, 1.0], [0, 1, 1, 1]);
          y = interpolate(p, [0.57, 0.70], [20, 0]);
        }

        slide.style.opacity = opacity;
        slide.style.transform = `translateY(${y}px)`;
        slide.style.display = opacity > 0.001 ? 'flex' : 'none';
      });

      if (Math.abs(targetProgress - currentProgress) > 0.0001) {
        requestAnimationFrame(update);
        ticking = true;
      } else ticking = false;
    }

    function requestTick() {
      const rect = appShowcase.getBoundingClientRect();
      const stickyOffset = 72;
      const scrollHeight = rect.height - window.innerHeight;
      let progress = (stickyOffset - rect.top) / scrollHeight;
      targetProgress = Math.max(0, Math.min(1, progress));

      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);
    setTimeout(requestTick, 100);
  }



  /**
   * Setup generic scroll animations
   */
  function setupScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '-50px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all elements with animate-on-scroll class
    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });

    // Observe lazy-loaded elements
    document.querySelectorAll('[data-animate-on-scroll]').forEach(el => {
      observer.observe(el);
    });
  }

  // Shopify section reload support
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', function () {
      console.log('🔄 Shopify section reloaded');
      setTimeout(() => {
        setupWatchScene();
        setupAppShowcase();
      }, 100);
    });
  }

})();

console.log('✅ TremCare animations script loaded');
